import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { queryClient } from "@/pages/Root";
import { del, get } from "api";

import { invalidateCurrentOrderDetails } from "./useCartDetails";

interface UseManageCartAppointmentsProps {
  cartDetails:
    | {
        selectedLocationId?: string | null;
        shopDisplayOrderLineItems?: Array<{
          id: string;
          appointmentId?: string | null;
        }> | null;
      }
    | null
    | undefined;
  partnerEmrBookingEnabled?: boolean;
}

export const useManageCartAppointments = ({
  cartDetails,
  partnerEmrBookingEnabled,
}: UseManageCartAppointmentsProps) => {
  const navigate = useNavigate();
  const { data: pendingAppointments } = useQuery({
    queryKey: [
      "/platform-location/{locationId}/pending-appointments",
      cartDetails?.selectedLocationId,
    ],
    queryFn: () =>
      get("/platform-location/{locationId}/pending-appointments", {
        locationId: cartDetails?.selectedLocationId ?? "",
      }),
    enabled: !!cartDetails?.selectedLocationId && !!partnerEmrBookingEnabled,
  });

  // Calculate reservation time remaining countdown
  const [reservationTimeRemaining, setReservationTimeRemaining] = useState<{
    minutes: number;
    seconds: number;
  } | null>(null);

  // Track if line items are being deleted
  const [isLineItemBeingDeleted, setIsLineItemBeingDeleted] = useState(false);

  const removeExpiredAppointments = useCallback(async () => {
    setReservationTimeRemaining(null);
    // Review all line items and remove any that:
    // 1. Has an appointmentId that is not null AND
    // 2. The appointmentId is not in the pendingAppointments array OR the pendingAppointment from the line item has a finalExpirDateTime that is in the past

    if (!cartDetails?.shopDisplayOrderLineItems || !pendingAppointments) {
      return;
    }

    const now = new Date();
    const itemsToRemove: string[] = [];

    for (const cartLineItem of cartDetails.shopDisplayOrderLineItems) {
      // appointmentId might be stored on the cart line item or need to be checked from lineItems
      // Check if line item has an appointmentId that is not null
      // Using type assertion to access appointmentId which may exist at runtime
      const appointmentId = (cartLineItem as { appointmentId?: string | null })
        .appointmentId;

      if (!appointmentId) {
        continue;
      }

      // Find the corresponding pending appointment
      // pendingAppointments use appointmentId as the identifier field
      const pendingAppointment = pendingAppointments.find(
        (appointment) => appointment.appointmentId === appointmentId
      );

      // Check if appointmentId is not in pendingAppointments OR has expired
      const shouldRemove =
        !pendingAppointment ||
        (pendingAppointment.finalExpiryDateTime &&
          new Date(pendingAppointment.finalExpiryDateTime) < now);

      if (shouldRemove) {
        itemsToRemove.push(cartLineItem.id);
      }
    }

    // Delete all expired items in parallel
    if (itemsToRemove.length > 0) {
      setIsLineItemBeingDeleted(true);
      try {
        await Promise.all(
          itemsToRemove.map((shopDisplayOrderLineItemId) =>
            del("/orders/current/lineitems/{shopDisplayOrderLineItemId}", {
              shopDisplayOrderLineItemId,
            })
          )
        );

        // Invalidate queries after deletion
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["/orders/current/lineitems"],
          }),
          invalidateCurrentOrderDetails(),
        ]);

        // Navigate to cart after invalidating queries
        navigate("/cart");
      } finally {
        setIsLineItemBeingDeleted(false);
      }
    }
  }, [cartDetails?.shopDisplayOrderLineItems, pendingAppointments, navigate]);

  useEffect(() => {
    if (!pendingAppointments || pendingAppointments.length === 0) {
      removeExpiredAppointments();
      return;
    }

    // Find the appointment with the lowest expiryTime
    const appointmentWithLowestExpiry = pendingAppointments.reduce(
      (earliest, current) => {
        if (!earliest.finalExpiryDateTime) return current;
        if (!current.finalExpiryDateTime) return earliest;
        return new Date(current.finalExpiryDateTime) <
          new Date(earliest.finalExpiryDateTime)
          ? current
          : earliest;
      }
    );

    if (!appointmentWithLowestExpiry.finalExpiryDateTime) {
      removeExpiredAppointments();
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const expiryTimeString = appointmentWithLowestExpiry.finalExpiryDateTime;
      if (!expiryTimeString) {
        removeExpiredAppointments();
        return;
      }
      const expiryTime = new Date(expiryTimeString);
      const timeDiff = expiryTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        removeExpiredAppointments();
        return;
      }

      const minutes = Math.floor(timeDiff / (1000 * 60));
      const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      setReservationTimeRemaining({ minutes, seconds });
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [pendingAppointments, removeExpiredAppointments]);

  return {
    reservationTimeRemaining,
    isLineItemBeingDeleted,
  };
};
